/**
 * Vincent B658 (Eric 2026-08-05 SPEC STRICK PROD) — Helpers UI qui
 * REMPLACENT les 132 `alert()` / `window.confirm()` / `prompt()` du code
 * frontend par des toasts Sonner (identité visuelle KNORIA), et évitent
 * le rebond du navigateur natif (dialogue bloquant, style OS).
 *
 * Usage :
 *   // Ancien : alert("Message")
 *   toast.success("Message");            // ou .error / .info selon le cas.
 *
 *   // Ancien : if (!window.confirm("Confirmer ?")) return;
 *   const ok = await confirmDialog("Confirmer ?");
 *   if (!ok) return;
 *
 *   // Ancien : const val = prompt("Raison ?");
 *   const val = await promptDialog("Raison ?");
 *   if (!val) return;
 *
 * Pourquoi Sonner et pas une modale full-blown ? Sonner est déjà mounted
 * globalement dans App.js (<Toaster />), zéro nouvelle dépendance, style
 * cohérent avec le reste de l'app, non-bloquant (l'UI reste utilisable).
 * Pour des confirmations DESTRUCTIVES ou complexes (ex. suppression avec
 * saisie du nom exact), continuer à utiliser un composant AlertDialog
 * shadcn dédié.
 *
 * ESLint : la règle `no-alert` est passée à `error` dans `.eslintrc.js`.
 * Tout nouveau `alert() / confirm() / prompt()` cassera le build → force
 * l'usage de ces helpers.
 */
import React from "react";
import { toast } from "sonner";
import { Info, Warning, Trash } from "@phosphor-icons/react";

/**
 * Remplace `window.confirm(msg)`.
 * @param {string} message - Question posée à l'utilisateur.
 * @param {{ confirmLabel?: string, cancelLabel?: string, duration?: number, danger?: boolean, tone?: "info"|"warning"|"destructive" }} opts
 * @returns {Promise<boolean>} true si l'utilisateur a cliqué « Confirmer ».
 *
 * Vincent B739 (Eric 2026-02-14) — Les boutons sont EMPILÉS verticalement
 * (Confirmer AU-DESSUS, Annuler EN DESSOUS) au lieu du layout Sonner par
 * défaut (côte à côte, à droite). Ça libère de la largeur pour le message,
 * essentiel pour les confirmations multi-lignes (« Refaire Whisper »,
 * « Refusionner », etc.).
 *
 * Vincent B739 v2 (Eric 2026-02-14) — Icône contextuelle en tête de dialog :
 *   - `tone: "info"` (défaut) → ℹ️ bleu (Info)
 *   - `tone: "warning"`       → ⚠️ orange (Warning)
 *   - `tone: "destructive"`   → 🗑️ rouge (Trash)
 * Rétro-compat : `danger: true` mappe automatiquement à `tone: "destructive"`.
 *
 * Implémentation : `toast.custom((id) => <JSX>)` pour contrôler le layout.
 * On préserve les newlines du message (`whitespace-pre-line`) et on rend
 * les boutons full-width avec l'action principale en violet-clair KNORIA
 * (ou rouge si `tone="destructive"`).
 */
const _TONE_PRESETS = {
  info: {
    Icon: Info,
    iconClass: "text-blue-600",
    iconWrapClass: "bg-blue-100",
    borderClass: "border-blue-200",
    confirmBtnClass: "bg-violet-600 hover:bg-violet-700",
  },
  warning: {
    Icon: Warning,
    iconClass: "text-orange-600",
    iconWrapClass: "bg-orange-100",
    borderClass: "border-orange-300",
    confirmBtnClass: "bg-orange-600 hover:bg-orange-700",
  },
  destructive: {
    Icon: Trash,
    iconClass: "text-red-600",
    iconWrapClass: "bg-red-100",
    borderClass: "border-red-300",
    confirmBtnClass: "bg-red-600 hover:bg-red-700",
  },
};

export function confirmDialog(message, opts = {}) {
  // Vincent B802 (Eric 2026-02-13) — Support d'une signature ALTERNATIVE
  // par objet : `confirmDialog({ title, message|description, confirmLabel,
  // cancelLabel, danger, tone })`. Cette forme est utilisée par
  // UploadMaintenancePage et le bulk regenerate de ScriptsPage. Avant ce
  // fix, l'objet était passé comme `message` et React tentait de le
  // rendre → error #31 (« object with keys {title, description, confirmLabel} »).
  //
  // Rétro-compat totale : `confirmDialog("string", { opts })` continue de
  // marcher. Quand `message` est un objet, on extrait `title` et le vrai
  // corps du dialog (`description` OU `message`) et on merge les opts.
  let title = null;
  if (message && typeof message === "object" && !React.isValidElement(message)) {
    const obj = message;
    title = obj.title || null;
    // Le corps peut être fourni via `description` (nouvelle convention) ou
    // `message` (ancienne). React acceptera les 2.
    message = obj.description ?? obj.message ?? "";
    // Merge des opts éventuels passés dans l'objet (confirmLabel, etc.)
    opts = { ...obj, ...opts };
  }
  const {
    confirmLabel = "Confirmer",
    cancelLabel = "Annuler",
    duration = 15000,
    danger = false,
    tone,
  } = opts;
  // Rétro-compat : `danger=true` sans `tone` explicite → destructive.
  const effectiveTone = tone || (danger ? "destructive" : "info");
  const preset = _TONE_PRESETS[effectiveTone] || _TONE_PRESETS.info;
  const { Icon, iconClass, iconWrapClass, borderClass, confirmBtnClass } = preset;
  return new Promise((resolve) => {
    let settled = false;
    const settle = (val) => {
      if (settled) return;
      settled = true;
      // Ferme le toast quel que soit le choix
      try { toast.dismiss(toastId); } catch (_) { /* déjà fermé */ }
      resolve(val);
    };
    const toastId = toast.custom(
      (t) => (
        <div
          data-testid="confirm-dialog"
          data-tone={effectiveTone}
          className={`w-[360px] max-w-[92vw] rounded-lg border shadow-xl bg-white p-4 flex flex-col gap-3 ${borderClass}`}
          role="alertdialog"
          aria-modal="true"
        >
          <div className="flex items-start gap-3">
            <div
              data-testid="confirm-dialog-icon"
              className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${iconWrapClass}`}
            >
              <Icon size={20} weight="fill" className={iconClass} />
            </div>
            <div
              data-testid="confirm-dialog-message"
              className="flex-1 text-sm text-zinc-900 leading-relaxed whitespace-pre-line pt-1"
            >
              {title && (
                <div
                  data-testid="confirm-dialog-title"
                  className="font-semibold text-zinc-900 mb-1"
                >
                  {title}
                </div>
              )}
              {message}
            </div>
          </div>
          <div className="flex flex-col gap-2" data-testid="confirm-dialog-buttons">
            <button
              type="button"
              data-testid="confirm-dialog-confirm"
              onClick={() => settle(true)}
              className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold text-white shadow-sm ${confirmBtnClass}`}
            >
              {confirmLabel}
            </button>
            <button
              type="button"
              data-testid="confirm-dialog-cancel"
              onClick={() => settle(false)}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            >
              {cancelLabel}
            </button>
          </div>
        </div>
      ),
      {
        duration,
        // Auto-dismiss & clic hors toast comptent comme « annuler »
        onDismiss: () => settle(false),
        onAutoClose: () => settle(false),
      }
    );
  });
}

/**
 * Remplace `prompt(msg)`.
 * Version simple : ouvre un toast avec un champ input embarqué. Pour des
 * saisies complexes (multi-lignes, validation regex, etc.) utiliser une
 * modale AlertDialog dédiée.
 *
 * NB — Sonner ne permet pas d'inputs dans les toasts. On tombe donc sur
 * un modal minimaliste via `document.body` (portal DIY) qui garantit
 * une compatibilité 100 % sans nouveau composant lourd.
 *
 * @param {string} message
 * @param {{ defaultValue?: string, placeholder?: string, confirmLabel?: string, cancelLabel?: string }} opts
 * @returns {Promise<string|null>} valeur saisie, ou null si annulé.
 */
export function promptDialog(message, opts = {}) {
  const {
    defaultValue = "",
    placeholder = "",
    confirmLabel = "OK",
    cancelLabel = "Annuler",
  } = opts;
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("data-testid", "prompt-dialog-overlay");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);backdrop-filter:blur(4px);";

    const card = document.createElement("div");
    card.style.cssText =
      "background:white;border-radius:12px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,0.3);min-width:320px;max-width:480px;width:90%;font-family:system-ui,-apple-system,sans-serif;";

    const msg = document.createElement("p");
    msg.textContent = message;
    msg.style.cssText = "margin:0 0 16px;font-size:14px;color:#18181b;line-height:1.5;";

    const input = document.createElement("input");
    input.type = "text";
    input.value = defaultValue;
    input.placeholder = placeholder;
    input.setAttribute("data-testid", "prompt-dialog-input");
    input.style.cssText =
      "width:100%;box-sizing:border-box;padding:8px 12px;border:1px solid #d4d4d8;border-radius:6px;font-size:14px;margin-bottom:16px;outline:none;";
    input.addEventListener("focus", () => { input.style.borderColor = "#14b8a6"; });
    input.addEventListener("blur", () => { input.style.borderColor = "#d4d4d8"; });

    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px;justify-content:flex-end;";

    const btnCancel = document.createElement("button");
    btnCancel.textContent = cancelLabel;
    btnCancel.setAttribute("data-testid", "prompt-dialog-cancel");
    btnCancel.style.cssText =
      "padding:6px 14px;border:1px solid #d4d4d8;background:white;border-radius:6px;font-size:13px;cursor:pointer;color:#18181b;";

    const btnConfirm = document.createElement("button");
    btnConfirm.textContent = confirmLabel;
    btnConfirm.setAttribute("data-testid", "prompt-dialog-confirm");
    btnConfirm.style.cssText =
      "padding:6px 14px;border:1px solid #14b8a6;background:#14b8a6;color:white;border-radius:6px;font-size:13px;cursor:pointer;font-weight:600;";

    let settled = false;
    const cleanup = (val) => {
      if (settled) return;
      settled = true;
      try { document.body.removeChild(overlay); } catch (_) { /* already removed */ }
      resolve(val);
    };

    btnCancel.addEventListener("click", () => cleanup(null));
    btnConfirm.addEventListener("click", () => cleanup(input.value));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") cleanup(input.value);
      if (e.key === "Escape") cleanup(null);
    });
    overlay.addEventListener("click", (e) => { if (e.target === overlay) cleanup(null); });

    btnRow.appendChild(btnCancel);
    btnRow.appendChild(btnConfirm);
    card.appendChild(msg);
    card.appendChild(input);
    card.appendChild(btnRow);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    setTimeout(() => input.focus(), 50);
  });
}
